import { useState, useCallback, type ReactNode, type Dispatch, type SetStateAction } from 'react';
import type { Spell, Item, ClassFeature, Feat } from '../types';
import { fetchSpellsFromSheets, fetchItemsFromSheets, fetchClassesFromSheets, fetchFeatsFromSheets, fetchFromSheetsUnified, fetchFromSheetsMultiTab, COMBINED_TEMPLATE_URL, SPELL_TEMPLATE_URL, ITEM_TEMPLATE_URL, CLASS_TEMPLATE_URL, FEAT_TEMPLATE_URL, type FetchSheetsResult } from '../utils/googleSheets';
import { ImportContext } from './importContextDef';
import {
  loadCustomSpells,
  loadCustomItems,
  loadCustomClasses,
  loadCustomFeats,
  saveCustomSpells,
  saveCustomItems,
  saveCustomClasses,
  saveCustomFeats,
  clearCustomSpells as clearStoredSpells,
  clearCustomItems as clearStoredItems,
  clearCustomClasses as clearStoredClasses,
  clearCustomFeats as clearStoredFeats,
} from '../utils/localStorage';

interface ImportState {
  isLoading: boolean;
  error: string | null;
  successCount: number | null;
}

interface UnifiedImportState extends ImportState {
  detectedType: 'spells' | 'items' | 'classes' | 'feats' | null;
}

interface MultiTabImportCounts {
  spells: number;
  items: number;
  classes: number;
  feats: number;
}

interface MultiTabImportState {
  isLoading: boolean;
  error: string | null;
  successCounts: MultiTabImportCounts | null;
  warnings: string[];
}

const INITIAL_IMPORT_STATE: ImportState = {
  isLoading: false,
  error: null,
  successCount: null,
};

const INITIAL_UNIFIED_IMPORT_STATE: UnifiedImportState = {
  isLoading: false,
  error: null,
  successCount: null,
  detectedType: null,
};

const INITIAL_MULTI_TAB_IMPORT_STATE: MultiTabImportState = {
  isLoading: false,
  error: null,
  successCounts: null,
  warnings: [],
};

/**
 * Creates an importer function that handles the common import logic:
 * fetch, dedupe, update state, save to localStorage, and report success/errors.
 */
function createImporter<T>(
  fetcher: (url: string) => Promise<FetchSheetsResult<T>>,
  setData: Dispatch<SetStateAction<T[]>>,
  setState: Dispatch<SetStateAction<ImportState>>,
  getDedupeKey: (item: T) => string,
  saveToStorage: (items: T[]) => void,
): (url: string) => Promise<void> {
  return async (url: string) => {
    setState({ isLoading: true, error: null, successCount: null });

    const result = await fetcher(url);

    if (result.errors.length > 0 && result.data.length === 0) {
      setState({ isLoading: false, error: result.errors[0], successCount: null });
    } else {
      setData(prev => {
        const existingKeys = new Set(prev.map(getDedupeKey));
        const newItems = result.data.filter(item => !existingKeys.has(getDedupeKey(item)));
        const updated = [...prev, ...newItems];
        saveToStorage(updated);
        return updated;
      });

      setState({
        isLoading: false,
        error: result.errors.length > 0 ? `Imported with warnings: ${result.errors[0]}` : null,
        successCount: result.data.length,
      });
    }
  };
}

export function ImportProvider({ children }: { children: ReactNode }) {
  // Initialize state from localStorage using lazy initializers
  const [customSpells, setCustomSpells] = useState<Spell[]>(loadCustomSpells);
  const [customItems, setCustomItems] = useState<Item[]>(loadCustomItems);
  const [customClasses, setCustomClasses] = useState<ClassFeature[]>(loadCustomClasses);
  const [customFeats, setCustomFeats] = useState<Feat[]>(loadCustomFeats);

  const [spellImportState, setSpellImportState] = useState<ImportState>(INITIAL_IMPORT_STATE);
  const [itemImportState, setItemImportState] = useState<ImportState>(INITIAL_IMPORT_STATE);
  const [classImportState, setClassImportState] = useState<ImportState>(INITIAL_IMPORT_STATE);
  const [featImportState, setFeatImportState] = useState<ImportState>(INITIAL_IMPORT_STATE);
  const [unifiedImportState, setUnifiedImportState] = useState<UnifiedImportState>(INITIAL_UNIFIED_IMPORT_STATE);
  const [multiTabImportState, setMultiTabImportState] = useState<MultiTabImportState>(INITIAL_MULTI_TAB_IMPORT_STATE);

  const importSpells = createImporter(
    fetchSpellsFromSheets,
    setCustomSpells,
    setSpellImportState,
    (s: Spell) => s.name.toLowerCase(),
    saveCustomSpells,
  );

  const importItems = createImporter(
    fetchItemsFromSheets,
    setCustomItems,
    setItemImportState,
    (i: Item) => i.name.toLowerCase(),
    saveCustomItems,
  );

  const importClasses = createImporter(
    fetchClassesFromSheets,
    setCustomClasses,
    setClassImportState,
    (f: ClassFeature) => `${f.name.toLowerCase()}|${f.className.toLowerCase()}`,
    saveCustomClasses,
  );

  const importFeats = createImporter(
    fetchFeatsFromSheets,
    setCustomFeats,
    setFeatImportState,
    (f: Feat) => f.name.toLowerCase(),
    saveCustomFeats,
  );

  const clearSpellImportStatus = useCallback(() => {
    setSpellImportState(prev => ({ ...prev, error: null, successCount: null }));
  }, []);

  const clearItemImportStatus = useCallback(() => {
    setItemImportState(prev => ({ ...prev, error: null, successCount: null }));
  }, []);

  const clearClassImportStatus = useCallback(() => {
    setClassImportState(prev => ({ ...prev, error: null, successCount: null }));
  }, []);

  const clearFeatImportStatus = useCallback(() => {
    setFeatImportState(prev => ({ ...prev, error: null, successCount: null }));
  }, []);

  // Clear all imported data functions
  const clearCustomSpells = useCallback(() => {
    setCustomSpells([]);
    clearStoredSpells();
  }, []);

  const clearCustomItems = useCallback(() => {
    setCustomItems([]);
    clearStoredItems();
  }, []);

  const clearCustomClasses = useCallback(() => {
    setCustomClasses([]);
    clearStoredClasses();
  }, []);

  const clearCustomFeats = useCallback(() => {
    setCustomFeats([]);
    clearStoredFeats();
  }, []);

  // Unified import that auto-detects type
  const importUnified = useCallback(async (url: string) => {
    setUnifiedImportState({ isLoading: true, error: null, successCount: null, detectedType: null });

    const result = await fetchFromSheetsUnified(url);

    if (result.type === null) {
      setUnifiedImportState({
        isLoading: false,
        error: result.errors[0] || 'Unknown error',
        successCount: null,
        detectedType: null,
      });
      return;
    }

    if (result.type === 'spells') {
      const spells = result.data as Spell[];
      setCustomSpells(prev => {
        const existingKeys = new Set(prev.map(s => s.name.toLowerCase()));
        const newItems = spells.filter(s => !existingKeys.has(s.name.toLowerCase()));
        const updated = [...prev, ...newItems];
        saveCustomSpells(updated);
        return updated;
      });

      setUnifiedImportState({
        isLoading: false,
        error: result.errors.length > 0 ? `Imported with warnings: ${result.errors[0]}` : null,
        successCount: spells.length,
        detectedType: 'spells',
      });
    } else if (result.type === 'items') {
      const items = result.data as Item[];
      setCustomItems(prev => {
        const existingKeys = new Set(prev.map(i => i.name.toLowerCase()));
        const newItems = items.filter(i => !existingKeys.has(i.name.toLowerCase()));
        const updated = [...prev, ...newItems];
        saveCustomItems(updated);
        return updated;
      });

      setUnifiedImportState({
        isLoading: false,
        error: result.errors.length > 0 ? `Imported with warnings: ${result.errors[0]}` : null,
        successCount: items.length,
        detectedType: 'items',
      });
    } else if (result.type === 'classes') {
      const classes = result.data as ClassFeature[];
      setCustomClasses(prev => {
        const existingKeys = new Set(prev.map(f => `${f.name.toLowerCase()}|${f.className.toLowerCase()}`));
        const newItems = classes.filter(f => !existingKeys.has(`${f.name.toLowerCase()}|${f.className.toLowerCase()}`));
        const updated = [...prev, ...newItems];
        saveCustomClasses(updated);
        return updated;
      });

      setUnifiedImportState({
        isLoading: false,
        error: result.errors.length > 0 ? `Imported with warnings: ${result.errors[0]}` : null,
        successCount: classes.length,
        detectedType: 'classes',
      });
    } else {
      // result.type === 'feats'
      const feats = result.data as Feat[];
      setCustomFeats(prev => {
        const existingKeys = new Set(prev.map(f => f.name.toLowerCase()));
        const newItems = feats.filter(f => !existingKeys.has(f.name.toLowerCase()));
        const updated = [...prev, ...newItems];
        saveCustomFeats(updated);
        return updated;
      });

      setUnifiedImportState({
        isLoading: false,
        error: result.errors.length > 0 ? `Imported with warnings: ${result.errors[0]}` : null,
        successCount: feats.length,
        detectedType: 'feats',
      });
    }
  }, []);

  const importMultiTab = useCallback(async (url: string) => {
    setMultiTabImportState({
      isLoading: true,
      error: null,
      successCounts: null,
      warnings: [],
    });

    const result = await fetchFromSheetsMultiTab(url);

    const hasFetchedData =
      result.data.spells.length > 0 ||
      result.data.items.length > 0 ||
      result.data.classes.length > 0 ||
      result.data.feats.length > 0;

    if (result.errors.length > 0 && !hasFetchedData) {
      setMultiTabImportState({
        isLoading: false,
        error: result.errors[0],
        successCounts: null,
        warnings: [],
      });
      return;
    }

    let spellCount = 0;
    let itemCount = 0;
    let classCount = 0;
    let featCount = 0;

    setCustomSpells((prev) => {
      const existingKeys = new Set(prev.map((s) => s.name.toLowerCase()));
      const newItems = result.data.spells.filter((s) => !existingKeys.has(s.name.toLowerCase()));
      spellCount = newItems.length;
      const updated = [...prev, ...newItems];
      saveCustomSpells(updated);
      return updated;
    });

    setCustomItems((prev) => {
      const existingKeys = new Set(prev.map((i) => i.name.toLowerCase()));
      const newItems = result.data.items.filter((i) => !existingKeys.has(i.name.toLowerCase()));
      itemCount = newItems.length;
      const updated = [...prev, ...newItems];
      saveCustomItems(updated);
      return updated;
    });

    setCustomClasses((prev) => {
      const existingKeys = new Set(prev.map((f) => `${f.name.toLowerCase()}|${f.className.toLowerCase()}`));
      const newItems = result.data.classes.filter(
        (f) => !existingKeys.has(`${f.name.toLowerCase()}|${f.className.toLowerCase()}`)
      );
      classCount = newItems.length;
      const updated = [...prev, ...newItems];
      saveCustomClasses(updated);
      return updated;
    });

    setCustomFeats((prev) => {
      const existingKeys = new Set(prev.map((f) => f.name.toLowerCase()));
      const newItems = result.data.feats.filter((f) => !existingKeys.has(f.name.toLowerCase()));
      featCount = newItems.length;
      const updated = [...prev, ...newItems];
      saveCustomFeats(updated);
      return updated;
    });

    const warnings = [
      ...result.errors,
      ...result.errorsByType.spells.map((error) => `Spells: ${error}`),
      ...result.errorsByType.items.map((error) => `Items: ${error}`),
      ...result.errorsByType.classes.map((error) => `Class Features: ${error}`),
      ...result.errorsByType.feats.map((error) => `Feats: ${error}`),
    ];

    setMultiTabImportState({
      isLoading: false,
      error: null,
      successCounts: {
        spells: spellCount,
        items: itemCount,
        classes: classCount,
        feats: featCount,
      },
      warnings,
    });
  }, []);

  const clearUnifiedImportStatus = useCallback(() => {
    setUnifiedImportState(INITIAL_UNIFIED_IMPORT_STATE);
  }, []);

  const clearMultiTabImportStatus = useCallback(() => {
    setMultiTabImportState(INITIAL_MULTI_TAB_IMPORT_STATE);
  }, []);

  const importMultipleUrls = useCallback(async (urls: string[]) => {
    setMultiTabImportState({
      isLoading: true,
      error: null,
      successCounts: null,
      warnings: [],
    });

    const results = await Promise.all(urls.map(url => fetchFromSheetsMultiTab(url)));

    // Aggregate all data
    const allSpells: Spell[] = [];
    const allItems: Item[] = [];
    const allClasses: ClassFeature[] = [];
    const allFeats: Feat[] = [];
    const allWarnings: string[] = [];
    let noDataCount = 0;

    for (const result of results) {
      const hasData =
        result.data.spells.length > 0 ||
        result.data.items.length > 0 ||
        result.data.classes.length > 0 ||
        result.data.feats.length > 0;

      if (!hasData && result.errors.length > 0) {
        noDataCount++;
      }

      allSpells.push(...result.data.spells);
      allItems.push(...result.data.items);
      allClasses.push(...result.data.classes);
      allFeats.push(...result.data.feats);

      allWarnings.push(
        ...result.errors,
        ...result.errorsByType.spells.map((error) => `Spells: ${error}`),
        ...result.errorsByType.items.map((error) => `Items: ${error}`),
        ...result.errorsByType.classes.map((error) => `Class Features: ${error}`),
        ...result.errorsByType.feats.map((error) => `Feats: ${error}`),
      );
    }

    if (noDataCount === results.length) {
      setMultiTabImportState({
        isLoading: false,
        error: 'No supported tabs found in any selected sheet.',
        successCounts: null,
        warnings: [],
      });
      return;
    }

    let spellCount = 0;
    let itemCount = 0;
    let classCount = 0;
    let featCount = 0;

    setCustomSpells((prev) => {
      const existingKeys = new Set(prev.map((s) => s.name.toLowerCase()));
      const newItems = allSpells.filter((s) => !existingKeys.has(s.name.toLowerCase()));
      spellCount = newItems.length;
      const updated = [...prev, ...newItems];
      saveCustomSpells(updated);
      return updated;
    });

    setCustomItems((prev) => {
      const existingKeys = new Set(prev.map((i) => i.name.toLowerCase()));
      const newItems = allItems.filter((i) => !existingKeys.has(i.name.toLowerCase()));
      itemCount = newItems.length;
      const updated = [...prev, ...newItems];
      saveCustomItems(updated);
      return updated;
    });

    setCustomClasses((prev) => {
      const existingKeys = new Set(prev.map((f) => `${f.name.toLowerCase()}|${f.className.toLowerCase()}`));
      const newItems = allClasses.filter(
        (f) => !existingKeys.has(`${f.name.toLowerCase()}|${f.className.toLowerCase()}`)
      );
      classCount = newItems.length;
      const updated = [...prev, ...newItems];
      saveCustomClasses(updated);
      return updated;
    });

    setCustomFeats((prev) => {
      const existingKeys = new Set(prev.map((f) => f.name.toLowerCase()));
      const newItems = allFeats.filter((f) => !existingKeys.has(f.name.toLowerCase()));
      featCount = newItems.length;
      const updated = [...prev, ...newItems];
      saveCustomFeats(updated);
      return updated;
    });

    setMultiTabImportState({
      isLoading: false,
      error: null,
      successCounts: {
        spells: spellCount,
        items: itemCount,
        classes: classCount,
        feats: featCount,
      },
      warnings: allWarnings,
    });
  }, []);

  return (
    <ImportContext.Provider
      value={{
        customSpells,
        customItems,
        customClasses,
        customFeats,
        spellImportState,
        itemImportState,
        classImportState,
        featImportState,
        unifiedImportState,
        multiTabImportState,
        importSpells,
        importItems,
        importClasses,
        importFeats,
        importUnified,
        importMultiTab,
        importMultipleUrls,
        clearSpellImportStatus,
        clearItemImportStatus,
        clearClassImportStatus,
        clearFeatImportStatus,
        clearUnifiedImportStatus,
        clearMultiTabImportStatus,
        clearCustomSpells,
        clearCustomItems,
        clearCustomClasses,
        clearCustomFeats,
        combinedTemplateUrl: COMBINED_TEMPLATE_URL,
        spellTemplateUrl: SPELL_TEMPLATE_URL,
        itemTemplateUrl: ITEM_TEMPLATE_URL,
        classTemplateUrl: CLASS_TEMPLATE_URL,
        featTemplateUrl: FEAT_TEMPLATE_URL,
      }}
    >
      {children}
    </ImportContext.Provider>
  );
}
